/**
 * Service for authentication and authorization operations
 */
import { fetchAuthSession, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { AuthInfo } from '../App';
import type { Edulead } from 'edulead';
import { enumLabel, v4Error } from './edulead-v4';
import { eduleadV4 } from './edulead-v4-client';

class AuthService {
  
  /**
   * Fetch authentication information including user details and team data
   * This is the primary method for getting all auth-related data at app initialization
   */
  async fetchAuthInfo(): Promise<AuthInfo> {
    const currentSession = await fetchAuthSession({ forceRefresh: true });
    const currentAuthenticatedUser = await getCurrentUser();
    const userAttributes = await fetchUserAttributes();
    const accessToken = currentSession.tokens?.accessToken;
    
    // The caller's team. A Cognito group without a team record is NOT_FOUND; legacy answered
    // with an empty team, so the app starts without one (`team: null`) instead of failing.
    let team: Edulead.EduleadV4Team | null = null;
    try {
      team = (await eduleadV4.team.get()).team ?? null;
    } catch (err) {
      if (v4Error(err).reason !== 'NOT_FOUND') throw err;
    }
    const cognitoGroups = accessToken?.payload['cognito:groups'];

    const userId = currentSession.userSub as string;
    const teamId = cognitoGroups?.toString() as string;

    // Fetch permissions once at startup
    const { me } = await eduleadV4.me.get();

    return {
      teamId,
      userId,
      currentSession,
      currentAuthenticatedUser,
      userAttributes,
      team: team || null,
      // The API omits an empty list; the role is OWNER or MEMBER, as legacy's `profile || 'MEMBER'`.
      permissions: me?.permissions ?? [],
      role: enumLabel(me?.role, 'MEMBER_ROLE_') || 'MEMBER'
    };
  }
}

export const authService = new AuthService();